// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useForm } from 'react-hook-form'
import { Edit, Trash2, Plus, Home, Droplets, Zap } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { useTranslation } from 'react-i18next'
import { Button, Card, Badge, Modal, Input, Select, EmptyState, SetupGuide, Skeleton, ConfirmDialog } from '@/components/ui'
import { useRoomType, useDeleteRoomType } from '@/hooks/useRoomTypes'
import { useUnits, useDeleteUnit } from '@/hooks/useUnits'
import { useProperty } from '@/hooks/useProperties'
import { useCreateMeter } from '@/hooks/useMeters'

interface MeterFormData {
  meter_type: 'water' | 'electricity'
  serial_number: string
}

export function RoomTypeDetailPage() {
  const { t } = useTranslation('units')
  const { t: tm } = useTranslation('meters')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const roomTypeId = Number(id)

  const { data: roomType, isLoading, isFetching } = useRoomType(roomTypeId)
  const { data: property } = useProperty(roomType?.property_id || 0)
  const { data: units, isLoading: isLoadingUnits } = useUnits(roomTypeId)

  const deleteRoomType = useDeleteRoomType()
  const deleteUnit = useDeleteUnit()
  const createMeter = useCreateMeter()

  const [showMeterModal, setShowMeterModal] = useState(false)
  const [selectedUnitForMeter, setSelectedUnitForMeter] = useState<{ id: number; name: string } | null>(null)
  const [confirmDeleteRoomType, setConfirmDeleteRoomType] = useState(false)
  const [confirmDeleteUnit, setConfirmDeleteUnit] = useState<{ id: number; name: string } | null>(null)

  const meterForm = useForm<MeterFormData>()

  const handleDeleteRoomType = async () => {
    if (!roomType) return
    await deleteRoomType.mutateAsync({ id: roomTypeId, propertyId: roomType.property_id })
    navigate(`/properties/${roomType.property_id}`)
  }

  const handleDeleteUnit = async () => {
    if (!confirmDeleteUnit) return
    await deleteUnit.mutateAsync({ id: confirmDeleteUnit.id, roomTypeId })
    setConfirmDeleteUnit(null)
  }

  const openMeterModal = (unitId: number, unitName: string) => {
    meterForm.reset({ meter_type: 'water', serial_number: '' })
    setSelectedUnitForMeter({ id: unitId, name: unitName })
    setShowMeterModal(true)
  }

  const onCreateMeter = async (data: MeterFormData) => {
    if (!selectedUnitForMeter) return
    await createMeter.mutateAsync({
      unitId: selectedUnitForMeter.id,
      meter_type: data.meter_type,
      serial_number: data.serial_number || undefined,
    })
    setShowMeterModal(false)
    setSelectedUnitForMeter(null)
  }

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-32" />
          </Card>
        </div>
    )
  }

  if (!roomType) {
    return (
        <Card>
          <EmptyState
            title={t('room_type.not_found')}
            action={<Button onClick={() => navigate('/properties')}>{tc('nav.properties')}</Button>}
          />
        </Card>
    )
  }

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            { label: property?.name || tc('nav.properties'), href: `/properties/${roomType.property_id}` },
            { label: roomType.name },
          ]}
        />

        <PageHeader
          title={roomType.name}
          description={roomType.floor ? `${t('room_type.floor')} ${roomType.floor}` : undefined}
          isRefreshing={isFetching && !isLoading}
          primaryAction={{
            key: 'edit',
            label: tc('actions.edit'),
            icon: Edit,
            to: `/room-types/${roomTypeId}/edit`,
          }}
          overflowActions={[
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              destructive: true,
              onClick: () => setConfirmDeleteRoomType(true),
            },
          ]}
        />

        {/* Units */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">{t('labels.units')}</h2>
            <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate(`/room-types/${roomTypeId}/units/new`)}>
              {t('actions.add_unit')}
            </Button>
          </div>

          {isLoadingUnits ? (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-32" />
              ))}
            </div>
          ) : !units || units.length === 0 ? (
            <SetupGuide
              icon={<Home className="w-16 h-16" />}
              title={t('setup_guide.title')}
              description={t('setup_guide.description')}
              steps={[
                { title: t('setup_guide.step_1_title'), description: t('setup_guide.step_1_description') },
                { title: t('setup_guide.step_2_title'), description: t('setup_guide.step_2_description') },
              ]}
              action={
                <Button size="sm" leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate(`/room-types/${roomTypeId}/units/new`)}>
                  {t('actions.add_unit')}
                </Button>
              }
            />
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {units.map((unit) => (
                <div
                  key={unit.id}
                  className="p-4 border border-base-200 rounded-lg hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between mb-2">
                    <Link to={`/units/${unit.id}`} className="hover:text-primary">
                      <h4 className="font-semibold">{unit.name}</h4>
                      <p className="text-sm text-base-content/60">
                        {formatBaht(unit.price)}{tc('currency.per_month')}
                      </p>
                    </Link>
                    {/* EP-136 T136-020 — three-way status badge: vacant=success,
                        reserved=warning, occupied=ghost. */}
                    <Badge
                      variant={unit.status === 'vacant' ? 'success' : unit.status === 'reserved' ? 'warning' : 'ghost'}
                    >
                      {unit.status === 'vacant'
                        ? t('status.vacant')
                        : unit.status === 'reserved'
                          ? t('status.reserved')
                          : t('status.occupied')}
                    </Badge>
                  </div>

                  {/* Meters */}
                  <div className="flex items-center gap-2 mb-3">
                    {unit.meters?.map((meter) => (
                      <Link
                        key={meter.id}
                        to={`/meters/${meter.id}`}
                        className="flex items-center gap-1 text-xs px-2 py-1 bg-base-200 rounded hover:bg-base-300 transition-colors"
                        title={meter.serial_number || tm('empty.no_serial_number')}
                      >
                        {meter.meter_type === 'water' ? (
                          <Droplets className="w-3 h-3 text-info" />
                        ) : (
                          <Zap className="w-3 h-3 text-warning" />
                        )}
                        {tm(`types.${meter.meter_type}`)}
                      </Link>
                    ))}
                    <button
                      onClick={() => openMeterModal(unit.id, unit.name)}
                      className="flex items-center gap-1 text-xs px-2 py-1 border border-dashed border-base-300 rounded hover:border-primary hover:text-primary transition-colors"
                    >
                      <Plus className="w-3 h-3" />
                      {t('actions.meter')}
                    </button>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 pt-2 border-t border-base-200">
                    <Button
                      size="xs"
                      variant="ghost"
                      leftIcon={<Edit className="w-3 h-3" />}
                      onClick={() => navigate(`/units/${unit.id}/edit`)}
                    >
                      {tc('actions.edit')}
                    </Button>
                    <Button
                      size="xs"
                      variant="ghost"
                      className="text-error"
                      leftIcon={<Trash2 className="w-3 h-3" />}
                      onClick={() => setConfirmDeleteUnit({ id: unit.id, name: unit.name })}
                    >
                      {tc('actions.delete')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      <ConfirmDialog
        isOpen={confirmDeleteRoomType}
        onClose={() => setConfirmDeleteRoomType(false)}
        onConfirm={handleDeleteRoomType}
        title={tc('actions.delete')}
        message={t('messages.delete_room_type_confirm', { name: roomType?.name })}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteRoomType.isPending}
      />

      <ConfirmDialog
        isOpen={confirmDeleteUnit !== null}
        onClose={() => setConfirmDeleteUnit(null)}
        onConfirm={handleDeleteUnit}
        title={tc('actions.delete')}
        message={t('messages.delete_confirm', { name: confirmDeleteUnit?.name })}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteUnit.isPending}
      />

      {/* Add Meter Modal */}
      <Modal
        isOpen={showMeterModal}
        onClose={() => setShowMeterModal(false)}
        title={t('actions.add_meter_to_unit', { name: selectedUnitForMeter?.name })}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowMeterModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={meterForm.handleSubmit(onCreateMeter)} loading={createMeter.isPending}>
              {tm('actions.add_meter')}
            </Button>
          </>
        }
      >
        <form className="space-y-4">
          <Select
            label={tm('labels.meter_type')}
            options={[
              { value: 'water', label: tm('types.water') },
              { value: 'electricity', label: tm('types.electricity') },
            ]}
            {...meterForm.register('meter_type', { required: tm('validation.meter_type_required') })}
          />
          <Input
            label={tm('labels.serial_number_optional')}
            placeholder={tm('placeholders.serial_number')}
            {...meterForm.register('serial_number')}
          />
        </form>
      </Modal>
    </>
  )
}
